import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Eye, MoreHorizontal, Trash2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { CardOverlayDeleteButton } from '@/components/ui/CardOverlayDeleteButton'
import type { TestRowActionsProps } from '@/types/components/test-row-actions'

function DeleteConfirmPopoverContent({
  message,
  onConfirm,
  onCancel,
}: {
  message: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <PopoverContent
      side="top"
      align="end"
      className="w-auto p-3"
      onClick={(e) => e.stopPropagation()}
      // A confirmation must only be resolved by the user. Focus moving elsewhere
      // (the closing row menu, a re-render stealing focus) must never dismiss it;
      // Escape, an outside click, Cancel and Delete all still close it.
      onFocusOutside={(e) => e.preventDefault()}
    >
      <div className="flex flex-col gap-2">
        <Text size="sm">{message}</Text>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            data-testid="study-action-delete-confirm"
            onClick={onConfirm}
          >
            {t('delete', { ns: 'common' })}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="study-action-delete-cancel"
            onClick={onCancel}
          >
            {t('cancel', { ns: 'common' })}
          </Button>
        </div>
      </div>
    </PopoverContent>
  )
}

export function TestRowActions({
  isPlatformAdmin,
  isGlobal,
  onView,
  onRequestDelete,
  deleteOnly = false,
  actionSurface = 'row',
  triggerVariant = 'menu',
  viewLabel,
  menuAriaLabel,
  deleteLabel,
  onDuplicate,
  inlineConfirm = false,
  deleteConfirmMessage,
}: TestRowActionsProps) {
  const { t } = useTranslation(['app', 'common'])
  const resolvedViewLabel = viewLabel ?? t('view', { ns: 'common' })
  const resolvedDeleteLabel = deleteLabel ?? t('delete', { ns: 'common' })
  const resolvedDeleteConfirmMessage = deleteConfirmMessage ?? t('studyLobby.deleteDraftQ')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const pendingConfirmRef = useRef(false)

  const canDelete = isPlatformAdmin || !isGlobal
  const isDeleteTrigger = triggerVariant === 'delete'
  const isOverlay = actionSurface === 'overlay'

  const handleConfirmedDelete = () => {
    setConfirmOpen(false)
    onRequestDelete()
  }

  const handleDeleteClick = () => {
    if (inlineConfirm) {
      setConfirmOpen(true)
      return
    }
    onRequestDelete()
  }

  // From inside the menu the confirm cannot open yet: the menu still traps focus
  // while it animates out, so it would pull focus back off the popover and Radix
  // would dismiss it as a focus-outside. Open it once the menu has unmounted.
  const handleMenuDeleteClick = () => {
    if (inlineConfirm) {
      pendingConfirmRef.current = true
      return
    }
    onRequestDelete()
  }

  if (deleteOnly) {
    if (!canDelete) {
      return null
    }

    const deleteButton = (
      <CardOverlayDeleteButton
        dataTestId="study-actions-trigger"
        ariaLabel={resolvedDeleteLabel}
        onClick={(e) => {
          e.stopPropagation()
          handleDeleteClick()
        }}
      />
    )

    if (!inlineConfirm) {
      return deleteButton
    }

    return (
      <Popover open={confirmOpen} onOpenChange={setConfirmOpen}>
        <PopoverAnchor data-state={confirmOpen ? 'open' : 'closed'}>
          {deleteButton}
        </PopoverAnchor>
        <DeleteConfirmPopoverContent
          message={resolvedDeleteConfirmMessage}
          onConfirm={handleConfirmedDelete}
          onCancel={() => setConfirmOpen(false)}
        />
      </Popover>
    )
  }

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="study-actions-trigger"
          onClick={(e) => {
            e.stopPropagation()
          }}
          aria-label={
            isDeleteTrigger
              ? t('studyLobby.deleteStudyActions')
              : menuAriaLabel ?? t('studyLobby.studyActions')
          }
          className={cn(
            'cursor-pointer rounded-[5px] p-1.5 transition-colors',
            isDeleteTrigger
              ? 'text-white/90 hover:bg-destructive/80 hover:text-destructive-foreground'
              : isOverlay
                ? 'text-white/90 hover:bg-white/15 hover:text-white'
                : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground',
          )}
        >
          {isDeleteTrigger ? (
            <Trash2 className="h-4 w-4" />
          ) : (
            <MoreHorizontal className="h-4 w-4" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48"
        onCloseAutoFocus={(event) => {
          // Runs once the menu is unmounted, so nothing competes with the confirm
          // popover for focus. Skipping the default restore keeps focus off the
          // trigger, which sits outside the popover and would dismiss it.
          if (pendingConfirmRef.current) {
            pendingConfirmRef.current = false
            event.preventDefault()
            setConfirmOpen(true)
          }
        }}
      >
        <DropdownMenuItem
          data-testid="study-action-view"
          onClick={(e) => {
            e.stopPropagation()
            onView()
          }}
          className="cursor-pointer"
        >
          <Eye className="mr-2 h-4 w-4" />
          {resolvedViewLabel}
        </DropdownMenuItem>
        {onDuplicate && (
          <DropdownMenuItem
            data-testid="study-action-duplicate"
            onClick={(e) => {
              e.stopPropagation()
              onDuplicate()
            }}
            className="cursor-pointer"
          >
            <Copy className="mr-2 h-4 w-4" />
            {t('studyLobby.duplicate')}
          </DropdownMenuItem>
        )}
        {canDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="study-action-delete"
              onClick={(e) => {
                e.stopPropagation()
                handleMenuDeleteClick()
              }}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {resolvedDeleteLabel}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  if (!inlineConfirm) {
    return menu
  }

  return (
    <Popover open={confirmOpen} onOpenChange={setConfirmOpen}>
      <PopoverAnchor data-state={confirmOpen ? 'open' : 'closed'}>
        {menu}
      </PopoverAnchor>
      <DeleteConfirmPopoverContent
        message={resolvedDeleteConfirmMessage}
        onConfirm={handleConfirmedDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Popover>
  )
}
